import { motion } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import type { Achievement } from '@/config/achievementsConfig'
import { MedalIcon } from '../components/broadcast'
import {
  bannerSlideVariants,
  radialFlashVariants,
  SPRING_CELEBRATION,
  useMotionVariants,
} from '../motion/motionTokens'
import { CelebrationParticles } from './particles'

// TV lower-third: gold angled banner sliding in with a radial flash,
// a popping medal and a light particle burst. Clickable: opens the
// achievements gallery (Carrera → Logros).
export function AchievementBanner({ achievement, onOpen }: {
  achievement: Achievement
  onOpen?: () => void
}) {
  const banner = useMotionVariants(bannerSlideVariants)

  return (
    <motion.div
      className="achievement-banner"
      variants={banner}
      initial="initial"
      animate="enter"
      exit="exit"
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') onOpen() } : undefined}
      aria-label={onOpen ? `Ver logro ${achievement.name}` : undefined}
      style={onOpen ? { cursor: 'pointer' } : undefined}
    >
      <motion.div
        variants={radialFlashVariants}
        initial="initial"
        animate="enter"
        style={{
          position: 'absolute',
          inset: '-40px',
          background: 'radial-gradient(circle, rgba(255, 243, 196, 0.85) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />
      <CelebrationParticles />
      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ ...SPRING_CELEBRATION, delay: 0.15 }}
        style={{ position: 'relative', flex: 'none', display: 'flex' }}
      >
        <MedalIcon tier={achievement.tier} size={44} />
      </motion.div>
      <div style={{ position: 'relative', minWidth: 0 }}>
        <div className="achievement-banner-kicker">Logro desbloqueado</div>
        <div className="achievement-banner-name">{achievement.name}</div>
        {achievement.reward?.money && (
          <div className="achievement-banner-reward">
            +{achievement.reward.money.toLocaleString('es-ES')}€
          </div>
        )}
      </div>
      {onOpen && (
        <ChevronRight
          size={18}
          style={{ position: 'relative', flex: 'none', opacity: 0.7 }}
          aria-hidden
        />
      )}
    </motion.div>
  )
}
